import { autoinject } from 'aurelia-framework';
import { ReportTemplateRowType, IAccountClassRow, IAccountGroupRow, IFinancialSumRow, IPerformanceIndicatorRow, CommonRowsSet, IReportTemplateRow, FinancialEntryType, IFinancialEntryTypeRow } from '../models/report-template';
import { ResourceService } from '../../../utils/resourceService';


export interface ICommonRowOption {
	row: IReportTemplateRow;
	name: string;
	isCheckedByDefault: boolean;
	accountGroupSeparationAvailable?: boolean;
	accountSeparationAvailable?: boolean;
	sectionTitle?: string;
	includeInSelectAll?: boolean;
	typeSeparationAvailable?: boolean;
}

@autoinject
export class RowGeneratorCommonRows {

	constructor(private resourceService: ResourceService) { }

	private commonTypeSeparationRows: ICommonRowOption[] = [
		{
			name: this.resourceService.getResource('Reports_RowTitle_FinancialEntryType_Internal'),
			row: {
				type: ReportTemplateRowType.FinancialEntryType,

				// Types used for type separation
				types: [FinancialEntryType.InternalRevenue, FinancialEntryType.InternalExpenses],
			} as IFinancialEntryTypeRow,
			isCheckedByDefault: false,
			includeInSelectAll: true,
			accountSeparationAvailable: true,
			typeSeparationAvailable: true,
		},
		{
			name: this.resourceService.getResource('Reports_RowTitle_FinancialEntryType_PassedOn'),
			row: {
				type: ReportTemplateRowType.FinancialEntryType,

				// Types used for type separation
				types: [FinancialEntryType.PassedOnRevenue, FinancialEntryType.PassedOnExpenses],
			} as IFinancialEntryTypeRow,
			isCheckedByDefault: false,
			includeInSelectAll: true,
			accountSeparationAvailable: true,
			typeSeparationAvailable: true
		},
	];

	private commonPerformanceIndicatorRows: ICommonRowOption[] = [
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_RevenuePerInhabitant'),
			row: {
				type: ReportTemplateRowType.PerformanceIndicator,
				code: 'T_TUOTOT_ASUKKAAT'
			} as IPerformanceIndicatorRow,
			isCheckedByDefault: false, sectionTitle: this.resourceService.getResource("RowGeneratorIncludes_Heading_CommonPerformanceIndicators")
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_OperatingExpensesPerInhabitant'),
			row: {
				type: ReportTemplateRowType.PerformanceIndicator,
				code: 'T_TOIMKULUT_ASUKKAAT'
			} as IPerformanceIndicatorRow,
			isCheckedByDefault: false,
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_OperatingProfitPerInhabitant'),
			row: {
				type: ReportTemplateRowType.PerformanceIndicator,
				code: 'T_TOIMKATE_ASUKKAAT'
			} as IPerformanceIndicatorRow,
			isCheckedByDefault: false,
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_AllExpensesPerInhabitant'),
			row: {
				type: ReportTemplateRowType.PerformanceIndicator,
				code: 'T_KULUT_ASUKKAAT'
			} as IPerformanceIndicatorRow,
			isCheckedByDefault: false,
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_NetPerInhabitant'),
			row: {
				type: ReportTemplateRowType.PerformanceIndicator,
				code: 'T_NETTO_ASUKKAAT'
			} as IPerformanceIndicatorRow,
			isCheckedByDefault: false,
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_RevenueOfExpenses'),
			row: {
				type: ReportTemplateRowType.PerformanceIndicator,
				code: 'T_TUOTOT_KULUISTA'
			} as IPerformanceIndicatorRow,
			isCheckedByDefault: false,
		}];

	private basicSetRows: ICommonRowOption[] = [
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_Revenue'),
			row: {
				type: ReportTemplateRowType.AccountClass,
				code: 'TUOTOT',
			} as IAccountClassRow,
			isCheckedByDefault: true,
			accountGroupSeparationAvailable: true, accountSeparationAvailable: true, includeInSelectAll: true,
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_OperatingExpenses'),
			row: {
				type: ReportTemplateRowType.FinancialSum,
				performanceIndicator: 'T_TOIMKULUT',
			} as IFinancialSumRow,
			isCheckedByDefault: true,
			accountGroupSeparationAvailable: true, accountSeparationAvailable: true, includeInSelectAll: true,
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_OperatingProfit'),
			row: {
				type: ReportTemplateRowType.PerformanceIndicator,
				code: 'T_TOIMKATE',
			} as IPerformanceIndicatorRow,
			isCheckedByDefault: true,
			includeInSelectAll: true,
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_Depreciations'),
			row: {
				type: ReportTemplateRowType.AccountGroup,
				code: 'POISTOT',
			} as IAccountGroupRow,
			isCheckedByDefault: true,
			accountSeparationAvailable: true, includeInSelectAll: true,
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_Net'),
			row: {
				type: ReportTemplateRowType.FinancialSum,
				externalMunicipalitiesDivisionInclusion: 'Exclude'
			} as IFinancialSumRow,
			isCheckedByDefault: true,
			includeInSelectAll: true,
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_ExternalMunicipalitiesShare'),
			row: {
				type: ReportTemplateRowType.FinancialSum,
				externalMunicipalitiesDivisionInclusion: 'Only'
			} as IFinancialSumRow,
			isCheckedByDefault: false,
			includeInSelectAll: true,
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_WithExternalMunicipalitiesShare'),
			row: {
				type: ReportTemplateRowType.FinancialSum,
				externalMunicipalitiesDivisionInclusion: 'Include'
			} as IFinancialSumRow,
			isCheckedByDefault: false,
			includeInSelectAll: true,
		},
	]

	private incomeStatementSetRows: ICommonRowOption[] = [
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_IS_Revenue'),
			row: {
				type: ReportTemplateRowType.FinancialSum,
				performanceIndicator: 'T_TOIMINTATUOTOT_TL',
			} as IFinancialSumRow,
			isCheckedByDefault: true,
			accountGroupSeparationAvailable: true, accountSeparationAvailable: true, includeInSelectAll: true,
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_IS_ValmistusOmaanKayttoon'),
			row: {
				type: ReportTemplateRowType.AccountGroup,
				code: 'VALMISTUS_OMAAN_KÄYTTÖÖN',
			} as IAccountGroupRow,
			isCheckedByDefault: true,
			accountSeparationAvailable: true, includeInSelectAll: true,
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_IS_OperatingExpenses'),
			row: {
				type: ReportTemplateRowType.FinancialSum,
				performanceIndicator: 'T_TOIMKULUT',
			} as IFinancialSumRow,
			isCheckedByDefault: true,
			accountGroupSeparationAvailable: true, accountSeparationAvailable: true, includeInSelectAll: true,
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_IS_Toimintakate'),
			row: {
				type: ReportTemplateRowType.PerformanceIndicator,
				code: 'T_TOIMKATE',
			} as IPerformanceIndicatorRow,
			isCheckedByDefault: true,
			includeInSelectAll: true,
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_IS_Verotulot'),
			row: {
				type: ReportTemplateRowType.AccountGroup,
				code: 'VEROTULOT',
			} as IAccountGroupRow,
			isCheckedByDefault: true,
			accountSeparationAvailable: true, includeInSelectAll: true,
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_IS_Valtionosuudet'),
			row: {
				type: ReportTemplateRowType.AccountGroup,
				code: 'VALTIONOSUUDET',
			} as IAccountGroupRow,
			isCheckedByDefault: true,
			accountSeparationAvailable: true, includeInSelectAll: true,
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_IS_RahoitustuototJaKulut'),
			row: {
				type: ReportTemplateRowType.FinancialSum,
				performanceIndicator: 'T_RAHOITUSTUOTOT_KULUT',
			} as IFinancialSumRow,
			isCheckedByDefault: true,
			accountGroupSeparationAvailable: true, accountSeparationAvailable: true, includeInSelectAll: true,
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_IS_Vuosikate'),
			row: {
				type: ReportTemplateRowType.PerformanceIndicator,
				code: 'T_VUOSIKATE',
			} as IPerformanceIndicatorRow,
			isCheckedByDefault: true,
			includeInSelectAll: true,
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_IS_PoistotJaArvonalentumiset'),
			row: {
				type: ReportTemplateRowType.FinancialSum,
				performanceIndicator: 'T_POISTOT_JA_ARVONALENTUMISET',
			} as IFinancialSumRow,
			isCheckedByDefault: true,
			accountGroupSeparationAvailable: true, accountSeparationAvailable: true, includeInSelectAll: true,
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_IS_SatunnaisetErat'),
			row: {
				type: ReportTemplateRowType.FinancialSum,
				performanceIndicator: 'T_SATUNNAISET_TUOTOT_KULUT',
			} as IFinancialSumRow,
			isCheckedByDefault: true,
			accountGroupSeparationAvailable: true, accountSeparationAvailable: true, includeInSelectAll: true,
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_IS_TilikaudenTulos'),
			row: {
				type: ReportTemplateRowType.PerformanceIndicator,
				code: 'T_TULOS',
			} as IPerformanceIndicatorRow,
			isCheckedByDefault: true,
			includeInSelectAll: true,
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_IS_PoistoeronMuutos'),
			row: {
				type: ReportTemplateRowType.AccountGroup,
				code: 'POISTOERON_MUUTOS',
			} as IAccountGroupRow,
			isCheckedByDefault: true,
			accountSeparationAvailable: true, includeInSelectAll: true,
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_IS_VaraustenMuutos'),
			row: {
				type: ReportTemplateRowType.AccountGroup,
				code: 'VARAUSTEN_MUUTOS',
			} as IAccountGroupRow,
			isCheckedByDefault: true,
			accountSeparationAvailable: true, includeInSelectAll: true,
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_IS_RahastojenMuutos'),
			row: {
				type: ReportTemplateRowType.AccountGroup,
				code: 'RAHASTOJEN_MUUTOS',
			} as IAccountGroupRow,
			isCheckedByDefault: true,
			accountSeparationAvailable: true, includeInSelectAll: true,
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_IS_TilikaudenYlijaama'),
			row: {
				type: ReportTemplateRowType.PerformanceIndicator,
				code: 'T_YLIJÄÄMÄ',
			} as IPerformanceIndicatorRow,
			isCheckedByDefault: true,
			includeInSelectAll: true,
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_ExternalMunicipalitiesShare'),
			row: {
				type: ReportTemplateRowType.FinancialSum,
				externalMunicipalitiesDivisionInclusion: 'Only'
			} as IFinancialSumRow,
			isCheckedByDefault: false,
			includeInSelectAll: true,
		},
		{
			name: this.resourceService.getResource('RowGeneratorIncludes_CommonRows_WithExternalMunicipalitiesShare'),
			row: {
				type: ReportTemplateRowType.FinancialSum,
				externalMunicipalitiesDivisionInclusion: 'Include'
			} as IFinancialSumRow,
			isCheckedByDefault: false,
			includeInSelectAll: true,
		},
	];

	getOptions(category: CommonRowsSet): ICommonRowOption[] {
		switch (category) {
			case CommonRowsSet.Basic:
				return this.basicSetRows
					.concat(this.commonTypeSeparationRows)
					.concat(this.commonPerformanceIndicatorRows);

			case CommonRowsSet.IncomeStatement:
				return this.incomeStatementSetRows
					.concat(this.commonTypeSeparationRows)
					.concat(this.commonPerformanceIndicatorRows);

			default:
				throw new Error(`Unknown category '${category}'`);
		}
	}
}
